import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";

import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";

import {
  adminSettingPages,
  adminSettingPagesByPath,
} from "../../shared/admin-settings.mjs";
import { getCurrentUser, logout, type UserProfile } from "../data/auth";

import {
  disableAdminUser,
  createAdminUser,
  createAdminPlan,
  createAdminCoupon,
  getAdminDashboard,
  listAdminPlans,
  listAdminCoupons,
  listAdminUsers,
  listAdminSettings,
  listAdminSettingPages,
  saveAdminSetting,
  listAdminLogs,
  listAdminOrders,
  listAdminCarts,
  updateAdminUser,
  updateAdminPlan,
  testAdminEmailIntegration,
  type AdminPlan,
  type AdminUser,
  type AdminDashboard,
  type AdminCoupon,
  type AdminSetting,
  type AdminSettingPage,
  type AdminSettingFieldOption,
  type AdminLog,
  type AdminOrder,
  type AdminCart,
  updateAdminCoupon,
} from "../data/admin";

import { MiBoardDialog } from "./MiBoardDialog";
import { PasswordField } from "./PasswordField";
import { LoadingClock } from "./LoadingClock";

const sections = [
  {
    title: "Vendas",
    items: [
      ["/admin", "Dashboard de resultados"],
      ["/admin/plans", "Planos e produtos"],
      ["/admin/orders", "Pedidos"],
      ["/admin/users", "Usuários"],
      ["/admin/coupons", "Cupons"],
      ["/admin/carts", "Carrinhos"],
    ],
  },
  {
    title: "Sistema",
    items: [
      ["/admin/gateway", "Gateway de pagamento"],
      ["/admin/email", "Integração de e-mail"],
      ["/admin/email-templates", "Templates de e-mail"],
      ["/admin/gtm", "Google Tag Manager"],
      ["/admin/backups", "Backups"],
      ["/admin/security", "Segurança e IP"],
      ["/admin/system-logs", "Logs do sistema"],
      ["/admin/email-logs", "Logs de e-mail"],
      ["/admin/payment-logs", "Logs de pagamento"],
    ],
  },
];

const shellStyles: Record<string, CSSProperties> = {
  page: {
    minHeight: "100vh",
    display: "grid",
    gridTemplateColumns: "280px minmax(0, 1fr)",
    background:
      "linear-gradient(180deg, #f7fcfe 0%, #f4f8fa 38%, #eef5f8 100%)",
    color: "#102a35",
    fontFamily: "var(--font-family, Assistant, sans-serif)",
  },
  sidebar: {
    padding: 24,
    background: "linear-gradient(180deg, #025caf 0%, #004fc9 100%)",
    color: "white",
    boxShadow: "inset -1px 0 0 rgba(255,255,255,0.05)",
  },
  content: {
    padding: "40px min(6vw, 72px) 56px",
  },
  shellCard: {
    marginTop: 28,
    padding: 28,
    borderRadius: 20,
    background: "rgba(255,255,255,0.86)",
    border: "1px solid rgba(19,175,224,0.16)",
    boxShadow: "0 18px 50px rgba(16, 42, 53, 0.07)",
    backdropFilter: "blur(8px)",
  },
  sectionCard: {
    background: "var(--color-surface-lowest)",
    border: "1px solid var(--color-surface-high)",
    borderRadius: 16,
    padding: 18,
    boxShadow: "0 8px 24px rgba(16, 42, 53, 0.06)",
  },
  fieldCard: {
    display: "grid",
    gap: 8,
    padding: 16,
    borderRadius: 14,
    background: "var(--color-surface-low)",
    border: "1px solid var(--color-surface-high)",
  },
  label: {
    fontSize: 13,
    fontWeight: 700,
    color: "#55717b",
    letterSpacing: "0.03em",
    textTransform: "uppercase",
  },
  helper: {
    margin: 0,
    color: "#58727c",
    lineHeight: 1.5,
  },
  control: {
    width: "100%",
    boxSizing: "border-box" as const,
    borderRadius: 12,
    border: "1px solid var(--color-surface-high)",
    background: "var(--color-surface-lowest)",
    color: "#102a35",
    padding: "0.85rem 0.95rem",
    font: "inherit",
    outline: "none",
    transition: "border-color 120ms ease, box-shadow 120ms ease",
  },
  controlFocus: {
    borderColor: "var(--color-primary)",
    boxShadow: "0 0 0 3px rgba(5, 168, 255, 0.12)",
  },
  cardGrid: {
    display: "grid",
    gap: 16,
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  },
  actionRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 10,
  },
};

type UserDialogMode = "create" | "edit";
type PlanDialogMode = "create" | "edit";
type CouponDialogMode = "create" | "edit";
type EmailTemplateCatalogItem = {
  key: string;
  title: string;
  trigger: string;
  description: string;
  subjectKey: string;
  bodyKey: string;
  defaultSubject: string;
  defaultBody: string;
  variables: string[];
};

const emailTemplateCatalog: EmailTemplateCatalogItem[] = [
  {
    key: "signup",
    title: "Confirmação de cadastro",
    trigger: "Cadastro inicial do usuário",
    description:
      "Envia o link para confirmar a conta antes do primeiro acesso.",
    subjectKey: "signupSubject",
    bodyKey: "signupBody",
    defaultSubject: "Confirme sua conta no MiBoard",
    defaultBody:
      "Olá {{name}}, confirme seu cadastro acessando {{confirmLink}}.",
    variables: ["{{name}}", "{{confirmLink}}"],
  },
  {
    key: "welcome",
    title: "Boas-vindas",
    trigger: "Conta aprovada ou ativada",
    description: "Mensagem inicial para orientar o novo usuário.",
    subjectKey: "welcomeSubject",
    bodyKey: "welcomeBody",
    defaultSubject: "Bem-vindo ao MiBoard",
    defaultBody: "Olá {{name}}, seu acesso foi criado com sucesso.",
    variables: ["{{name}}", "{{loginUrl}}"],
  },
  {
    key: "reset",
    title: "Redefinição de senha",
    trigger: "Usuário solicitou recuperar acesso",
    description: "Usado no fluxo de 'esqueci minha senha'.",
    subjectKey: "resetSubject",
    bodyKey: "resetBody",
    defaultSubject: "Redefinição de senha",
    defaultBody: "Use este link para redefinir sua senha: {{resetLink}}",
    variables: ["{{resetLink}}"],
  },
  {
    key: "payment-success",
    title: "Pagamento aprovado",
    trigger: "Checkout confirmado pelo gateway",
    description: "Aviso de ativação ou confirmação de pagamento.",
    subjectKey: "paymentSuccessSubject",
    bodyKey: "paymentSuccessBody",
    defaultSubject: "Pagamento confirmado",
    defaultBody: "Seu plano foi ativado com sucesso.",
    variables: ["{{planName}}", "{{receiptUrl}}"],
  },
  {
    key: "payment-failed",
    title: "Pagamento recusado",
    trigger: "Falha de cobrança no gateway",
    description: "Mensagem para orientar nova tentativa de pagamento.",
    subjectKey: "paymentFailedSubject",
    bodyKey: "paymentFailedBody",
    defaultSubject: "Não conseguimos processar seu pagamento",
    defaultBody:
      "Revise seus dados de pagamento e tente novamente em {{checkoutUrl}}.",
    variables: ["{{checkoutUrl}}"],
  },
  {
    key: "renewal",
    title: "Renovação de assinatura",
    trigger: "Renovação automática ou manual",
    description: "Mantém o usuário informado sobre a continuidade do plano.",
    subjectKey: "subscriptionRenewalSubject",
    bodyKey: "subscriptionRenewalBody",
    defaultSubject: "Sua assinatura foi renovada",
    defaultBody: "Sua assinatura permanece ativa até {{currentPeriodEndsAt}}.",
    variables: ["{{currentPeriodEndsAt}}"],
  },
  {
    key: "canceled",
    title: "Assinatura cancelada",
    trigger: "Cancelamento da assinatura",
    description: "Mensagem de encerramento de acesso ou cobrança.",
    subjectKey: "subscriptionCanceledSubject",
    bodyKey: "subscriptionCanceledBody",
    defaultSubject: "Sua assinatura foi cancelada",
    defaultBody:
      "Se quiser voltar, seu acesso pode ser reativado a qualquer momento.",
    variables: ["{{supportUrl}}"],
  },
  {
    key: "invoice",
    title: "Fatura disponível",
    trigger: "Fatura gerada ou enviada",
    description: "Link para visualizar os detalhes do pagamento.",
    subjectKey: "invoiceReadySubject",
    bodyKey: "invoiceReadyBody",
    defaultSubject: "Sua fatura está disponível",
    defaultBody:
      "Acesse {{invoiceUrl}} para visualizar os detalhes do pagamento.",
    variables: ["{{invoiceUrl}}"],
  },
  {
    key: "trial-ending",
    title: "Teste terminando",
    trigger: "Usuário perto do fim do período gratuito",
    description: "Ajuda a converter o usuário antes da expiração do teste.",
    subjectKey: "trialEndingSubject",
    bodyKey: "trialEndingBody",
    defaultSubject: "Seu período de teste está terminando",
    defaultBody:
      "Seu acesso de teste termina em {{trialEndsAt}}. Faça upgrade para manter tudo ativo.",
    variables: ["{{trialEndsAt}}"],
  },
  {
    key: "account-disabled",
    title: "Conta desativada",
    trigger: "Admin desativou o usuário",
    description: "Comunicação administrativa sobre bloqueio de acesso.",
    subjectKey: "accountDisabledSubject",
    bodyKey: "accountDisabledBody",
    defaultSubject: "Sua conta foi desativada",
    defaultBody:
      "Sua conta foi desativada por um administrador. Se precisar, fale com o suporte.",
    variables: ["{{supportUrl}}"],
  },
];

const emptyUserDraft = {
  email: "",
  displayName: "",
  password: "",
  role: "member" as AdminUser["role"],
  status: "active" as AdminUser["status"],
};

const emptyPlanDraft = {
  code: "",
  name: "",
  description: "",
  monthlyPrice: "",
  annualPrice: "",
  features: "",
  active: true,
};

const emptyCouponDraft = {
  code: "",
  discountType: "percent" as AdminCoupon["discountType"],
  discountValue: "",
  maxUses: "",
  expiresAt: "",
  active: true,
};

export const AdminPage = () => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [error, setError] = useState("");
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [dashboard, setDashboard] = useState<AdminDashboard | null>(null);
  const [coupons, setCoupons] = useState<AdminCoupon[]>([]);
  const [settings, setSettings] = useState<AdminSetting[]>([]);
  const [settingPages, setSettingPages] = useState<AdminSettingPage[]>([]);
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [logsPage, setLogsPage] = useState(0);
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [carts, setCarts] = useState<AdminCart[]>([]);
  const settingsFormRef = useRef<HTMLFormElement | null>(null);
  const userFormRef = useRef<HTMLFormElement | null>(null);
  const planFormRef = useRef<HTMLFormElement | null>(null);
  const couponFormRef = useRef<HTMLFormElement | null>(null);
  const templateFormRef = useRef<HTMLFormElement | null>(null);
  const emailTestFormRef = useRef<HTMLFormElement | null>(null);
  const [userDialog, setUserDialog] = useState<{
    mode: UserDialogMode;
    item: AdminUser | null;
  } | null>(null);
  const [userDraft, setUserDraft] = useState(emptyUserDraft);
  const [planDialog, setPlanDialog] = useState<{
    mode: PlanDialogMode;
    item: AdminPlan | null;
  } | null>(null);
  const [planDraft, setPlanDraft] = useState(emptyPlanDraft);
  const [couponDialog, setCouponDialog] = useState<{
    mode: CouponDialogMode;
    item: AdminCoupon | null;
  } | null>(null);
  const [couponDraft, setCouponDraft] = useState(emptyCouponDraft);
  const [templateDialogKey, setTemplateDialogKey] = useState<string | null>(
    null,
  );
  const [emailTestDialogOpen, setEmailTestDialogOpen] = useState(false);
  const [emailTestRecipient, setEmailTestRecipient] = useState("");
  const [emailTestStatus, setEmailTestStatus] = useState<{
    kind: "success" | "error";
    message: string;
  } | null>(null);
  const [emailSettingsStatus, setEmailSettingsStatus] = useState<{
    kind: "success" | "error";
    message: string;
  } | null>(null);
  const path = window.location.pathname.replace(/^\/app/, "");
  const activeLabel = useMemo(() => {
    const menuLabel = sections
      .flatMap((section) => section.items)
      .find(([href]) => href === path)?.[1];
    if (menuLabel) {
      return menuLabel;
    }
    const pageKey =
      adminSettingPagesByPath[path as keyof typeof adminSettingPagesByPath];
    const pageTitle =
      (
        settingPages.find((page) => page.key === pageKey) as
          | AdminSettingPage
          | undefined
      )?.title ||
      (
        adminSettingPages.find((page) => page.key === pageKey) as
          | AdminSettingPage
          | undefined
      )?.title;
    return pageTitle || "Dashboard de resultados";
  }, [path, settingPages]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlHeight = html.style.height;
    const previousBodyHeight = body.style.height;

    html.style.overflow = "auto";
    body.style.overflow = "auto";
    html.style.height = "auto";
    body.style.height = "auto";

    getCurrentUser()
      .then((data) => {
        if (data.profile.role !== "admin") {
          window.location.assign("/app/boards");
          return;
        }
        setUser(data);
      })
      .catch(() =>
        setError("Não foi possível validar o acesso administrativo."),
      );

    return () => {
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      html.style.height = previousHtmlHeight;
      body.style.height = previousBodyHeight;
    };
  }, []);
  useEffect(() => {
    const isSettingsPage = sections
      .flatMap((section) => section.items)
      .some(([href]) => href === path && href !== "/admin");
    const isLogsPage = path.endsWith("-logs");
    const isDataPage = [
      "/admin",
      "/admin/users",
      "/admin/plans",
      "/admin/coupons",
      "/admin/orders",
      "/admin/carts",
    ].includes(path);
    if (!isDataPage && !isSettingsPage && !isLogsPage) {
      return;
    }
    if (isLogsPage) {
      listAdminLogs()
        .then(setLogs)
        .catch(() => setError("Não foi possível carregar os logs."));
      return;
    }
    if (path === "/admin/orders") {
      listAdminOrders()
        .then(setOrders)
        .catch(() => setError("Não foi possível carregar os pedidos."));
      return;
    }
    if (path === "/admin/carts") {
      listAdminCarts()
        .then(setCarts)
        .catch(() => setError("Não foi possível carregar os carrinhos."));
      return;
    }
    if (
      isSettingsPage &&
      !["/admin/users", "/admin/plans", "/admin/coupons"].includes(path)
    ) {
      listAdminSettings()
        .then(setSettings)
        .catch(() => setError("Não foi possível carregar as configurações."));
      return;
    }
    const load =
      path === "/admin/users"
        ? listAdminUsers
        : path === "/admin/plans"
        ? listAdminPlans
        : path === "/admin/coupons"
        ? listAdminCoupons
        : getAdminDashboard;
    load()
      .then((data) =>
        path === "/admin/users"
          ? setUsers(data as AdminUser[])
          : path === "/admin/plans"
          ? setPlans(data as AdminPlan[])
          : path === "/admin/coupons"
          ? setCoupons(data as AdminCoupon[])
          : setDashboard(data as AdminDashboard),
      )
      .catch(() =>
        setError("Não foi possível carregar os dados administrativos."),
      );
  }, [path]);
  useEffect(() => {
    listAdminSettingPages()
      .then(setSettingPages)
      .catch(() => {
        setSettingPages(adminSettingPages as AdminSettingPage[]);
      });
  }, []);
  const settingValue = (key: string) =>
    settings.find((setting) => setting.key === key)?.value || {};
  const saveSettings = async (key: string, value: Record<string, unknown>) => {
    const { setting } = await saveAdminSetting(key, value);
    setSettings((current) => [
      ...current.filter((item) => item.key !== key),
      setting,
    ]);
  };
  const currentEmailTemplateValue = settingValue("email_templates");
  const currentEmailSettings = settingValue("email");
  useEffect(() => {
    if (!userDialog) {
      return;
    }
    const source = userDialog.item;
    setUserDraft(
      source
        ? {
            email: source.email,
            displayName: source.displayName,
            password: "",
            role: source.role,
            status: source.status,
          }
        : emptyUserDraft,
    );
  }, [userDialog]);
  useEffect(() => {
    if (!planDialog) {
      return;
    }
    const source = planDialog.item;
    setPlanDraft(
      source
        ? {
            code: source.code,
            name: source.name,
            description: source.description,
            monthlyPrice: (source.monthlyPriceCents / 100).toFixed(2),
            annualPrice: (source.annualPriceCents / 100).toFixed(2),
            features: source.features.join("\n"),
            active: source.active,
          }
        : emptyPlanDraft,
    );
  }, [planDialog]);
  useEffect(() => {
    if (!couponDialog) {
      return;
    }
    const source = couponDialog.item;
    setCouponDraft(
      source
        ? {
            code: source.code,
            discountType: source.discountType,
            discountValue: String(source.discountValue),
            maxUses: source.maxUses == null ? "" : String(source.maxUses),
            expiresAt: source.expiresAt ? source.expiresAt.slice(0, 16) : "",
            active: source.active,
          }
        : emptyCouponDraft,
    );
  }, [couponDialog]);
  useEffect(() => {
    if (!emailTestDialogOpen) {
      return;
    }
    setEmailTestRecipient(
      user?.profile.email ||
        String(currentEmailSettings.senderEmail || "").trim() ||
        "",
    );
  }, [emailTestDialogOpen, user, currentEmailSettings]);
  const settingPage = () => {
    const pageKey =
      adminSettingPagesByPath[path as keyof typeof adminSettingPagesByPath];
    const page = (settingPages.find((item) => item.key === pageKey) ||
      adminSettingPages.find((item) => item.key === pageKey)) as
      | AdminSettingPage
      | undefined;
    if (!page) {
      return null;
    }
    if (page.key === "email_templates") {
      const templateCards = emailTemplateCatalog.map((template) => ({
        ...template,
        subject: String(
          currentEmailTemplateValue[template.subjectKey] ||
            template.defaultSubject,
        ),
        body: String(
          currentEmailTemplateValue[template.bodyKey] || template.defaultBody,
        ),
      }));
      const selectedTemplate =
        templateDialogKey &&
        templateCards.find((template) => template.key === templateDialogKey);
      return (
        <div className="miboard-admin-grid">
          <section className="miboard-admin-card miboard-admin-card--soft miboard-admin-card--pad">
            <div style={{ display: "grid", gap: 8 }}>
              <h2 style={{ margin: 0 }}>{page.title}</h2>
              <p className="miboard-admin-field__hint">{page.description}</p>
            </div>
          </section>
          <div className="miboard-admin-grid miboard-admin-grid--cards">
            {templateCards.map((template) => (
              <article
                key={template.key}
                className="miboard-admin-card miboard-admin-card--pad"
                style={{ display: "grid", gap: 12 }}
              >
                <div style={{ display: "grid", gap: 6 }}>
                  <strong style={{ fontSize: 18 }}>{template.title}</strong>
                  <small className="miboard-admin-field__hint">
                    {template.trigger}
                  </small>
                </div>
                <p className="miboard-admin-field__hint">
                  {template.description}
                </p>
                <div className="miboard-admin-card miboard-admin-card--soft miboard-admin-card--pad">
                  <small className="miboard-admin-field__label">Assunto</small>
                  <p style={{ margin: "6px 0 0" }}>{template.subject}</p>
                  <small className="miboard-admin-field__label">Corpo</small>
                  <p style={{ margin: "6px 0 0", whiteSpace: "pre-wrap" }}>
                    {template.body}
                  </p>
                </div>
                <small className="miboard-admin-field__hint">
                  Variáveis: {template.variables.join(", ")}
                </small>
                <div className="miboard-admin-button-row">
                  <FilledButton
                    onClick={() => setTemplateDialogKey(template.key)}
                    label="Editar template"
                    color="primary"
                    variant="outlined"
                  />
                </div>
              </article>
            ))}
          </div>
          {selectedTemplate && (
            <MiBoardDialog
              title={selectedTemplate.title}
              onClose={() => setTemplateDialogKey(null)}
              actions={
                <>
                  <button
                    type="button"
                    onClick={() => setTemplateDialogKey(null)}
                    className="miboard-cancel-button"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="miboard-save-button"
                    onClick={() => templateFormRef.current?.requestSubmit()}
                  >
                    Salvar template
                  </button>
                </>
              }
            >
              <form
                ref={templateFormRef}
                className="miboard-admin-grid"
                onSubmit={async (event) => {
                  event.preventDefault();
                  const form = new FormData(event.currentTarget);
                  const updated = {
                    ...currentEmailTemplateValue,
                    [selectedTemplate.subjectKey]: String(
                      form.get(selectedTemplate.subjectKey) || "",
                    ).trim(),
                    [selectedTemplate.bodyKey]: String(
                      form.get(selectedTemplate.bodyKey) || "",
                    ).trim(),
                  };
                  await saveSettings("email_templates", updated);
                  setTemplateDialogKey(null);
                }}
              >
                <label className="miboard-admin-field">
                  <span className="miboard-admin-field__label">Assunto</span>
                  <input
                    autoFocus
                    name={selectedTemplate.subjectKey}
                    className="miboard-admin-control"
                    defaultValue={selectedTemplate.subject}
                  />
                </label>
                <label className="miboard-admin-field">
                  <span className="miboard-admin-field__label">Corpo</span>
                  <textarea
                    name={selectedTemplate.bodyKey}
                    rows={8}
                    className="miboard-admin-control"
                    defaultValue={selectedTemplate.body}
                  />
                </label>
                <div className="miboard-admin-card miboard-admin-card--soft miboard-admin-card--pad">
                  <small className="miboard-admin-field__label">
                    Variáveis
                  </small>
                  <p
                    className="miboard-admin-field__hint"
                    style={{ marginTop: 8 }}
                  >
                    {selectedTemplate.variables.join(", ")}
                  </p>
                </div>
              </form>
            </MiBoardDialog>
          )}
        </div>
      );
    }
    const values = {
      ...Object.fromEntries(
        page.fields.map((field) => [field.name, field.defaultValue ?? ""]),
      ),
      ...settingValue(page.key),
    };
    return (
      <form
        ref={settingsFormRef}
        className="miboard-admin-grid"
        style={{ maxWidth: 760 }}
        onSubmit={async (event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const value = Object.fromEntries(
            page.fields.map((field) => {
              if (field.type === "boolean") {
                return [field.name, form.get(field.name) === "true"];
              }
              if (field.type === "number") {
                const rawValue = String(form.get(field.name) || "").trim();
                return [field.name, rawValue ? Number(rawValue) : 0];
              }
              if (Array.isArray(field.defaultValue)) {
                return [
                  field.name,
                  String(form.get(field.name) || "")
                    .split(/\r?\n/)
                    .map((item) => item.trim())
                    .filter(Boolean),
                ];
              }
              return [field.name, String(form.get(field.name) || "").trim()];
            }),
          );
          if (page.key === "email") {
            setEmailSettingsStatus(null);
          }
          try {
            await saveSettings(page.key, value);
            setError("");
            if (page.key === "email") {
              setEmailSettingsStatus({
                kind: "success",
                message:
                  "Configuração salva com segurança. A API Key não é exibida novamente.",
              });
            }
          } catch (saveError) {
            const message =
              saveError instanceof Error
                ? saveError.message
                : "Não foi possível salvar a configuração.";
            if (page.key === "email") {
              setEmailSettingsStatus({ kind: "error", message });
            } else {
              setError(message);
            }
          }
        }}
      >
        <section className="miboard-admin-card miboard-admin-card--soft miboard-admin-card--pad">
          <div style={{ display: "grid", gap: 8 }}>
            <h2 style={{ margin: 0 }}>{page.title}</h2>
            <p className="miboard-admin-field__hint">{page.description}</p>
          </div>
        </section>
        {page.fields.map((field) => {
          const currentValue = values[field.name];
          if (field.type === "textarea") {
            return (
              <label key={field.name} className="miboard-admin-field">
                <span className="miboard-admin-field__label">
                  {field.label}
                </span>
                <textarea
                  name={field.name}
                  rows={field.rows || 4}
                  placeholder={field.placeholder}
                  className="miboard-admin-control"
                  defaultValue={
                    Array.isArray(currentValue)
                      ? currentValue.join("\n")
                      : String(currentValue ?? "")
                  }
                />
              </label>
            );
          }
          if (field.type === "select") {
            return (
              <label key={field.name} className="miboard-admin-field">
                <span className="miboard-admin-field__label">
                  {field.label}
                </span>
                <select
                  name={field.name}
                  className="miboard-admin-control"
                  defaultValue={String(
                    currentValue ?? field.defaultValue ?? "",
                  )}
                >
                  {(field.options || []).map(
                    (option: AdminSettingFieldOption) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ),
                  )}
                </select>
              </label>
            );
          }
          if (field.type === "boolean") {
            return (
              <label key={field.name} className="miboard-admin-field">
                <span className="miboard-admin-field__label">
                  {field.label}
                </span>
                <select
                  name={field.name}
                  className="miboard-admin-control"
                  defaultValue={String(Boolean(currentValue))}
                >
                  <option value="true">Sim</option>
                  <option value="false">Não</option>
                </select>
              </label>
            );
          }
          return (
            <label key={field.name} className="miboard-admin-field">
              <span className="miboard-admin-field__label">{field.label}</span>
              {field.inputType === "password" ? (
                <PasswordField
                  name={field.name}
                  placeholder={
                    page.key === "email" &&
                    field.name === "apiKey" &&
                    currentEmailSettings.apiKeyConfigured
                      ? "Chave já configurada — deixe vazio para manter"
                      : field.placeholder
                  }
                  inputClassName="miboard-admin-control"
                  defaultValue={
                    page.key === "email" && field.name === "apiKey"
                      ? ""
                      : String(currentValue ?? "")
                  }
                />
              ) : (
                <input
                  name={field.name}
                  type={
                    field.inputType ||
                    (field.type === "number" ? "number" : "text")
                  }
                  placeholder={field.placeholder}
                  className="miboard-admin-control"
                  defaultValue={String(currentValue ?? "")}
                />
              )}
            </label>
          );
        })}
        {page.key === "email" && (
          <>
            {emailSettingsStatus && (
              <section
                className="miboard-admin-card miboard-admin-card--soft miboard-admin-card--pad"
                style={{
                  borderColor:
                    emailSettingsStatus.kind === "success"
                      ? "rgba(41, 179, 123, 0.28)"
                      : "rgba(220, 68, 68, 0.26)",
                }}
              >
                <strong
                  style={{
                    color:
                      emailSettingsStatus.kind === "success"
                        ? "#167d50"
                        : "#b42318",
                  }}
                >
                  {emailSettingsStatus.kind === "success"
                    ? "Configuração salva"
                    : "Não foi possível salvar"}
                </strong>
                <p
                  className="miboard-admin-field__hint"
                  style={{ marginTop: 8 }}
                >
                  {emailSettingsStatus.message}
                </p>
              </section>
            )}
            {emailTestStatus && (
              <section
                className="miboard-admin-card miboard-admin-card--soft miboard-admin-card--pad"
                style={{
                  borderColor:
                    emailTestStatus.kind === "success"
                      ? "rgba(41, 179, 123, 0.28)"
                      : "rgba(220, 68, 68, 0.26)",
                }}
              >
                <strong
                  style={{
                    color:
                      emailTestStatus.kind === "success"
                        ? "#167d50"
                        : "#b42318",
                  }}
                >
                  {emailTestStatus.kind === "success"
                    ? "Teste concluído"
                    : "Teste com erro"}
                </strong>
                <p
                  className="miboard-admin-field__hint"
                  style={{ marginTop: 8 }}
                >
                  {emailTestStatus.message}
                </p>
              </section>
            )}
            <div className="miboard-admin-button-row">
              <FilledButton
                onClick={() => settingsFormRef.current?.requestSubmit()}
                label="Salvar configuração"
                color="primary"
              />
              <FilledButton
                onClick={() => {
                  setEmailTestStatus(null);
                  setEmailTestRecipient(
                    user?.profile.email ||
                      String(currentEmailSettings.senderEmail || "").trim(),
                  );
                  setEmailTestDialogOpen(true);
                }}
                label="Testar integração"
                color="primary"
                variant="outlined"
              />
            </div>
          </>
        )}
        {page.key !== "email" && (
          <FilledButton
            onClick={() => settingsFormRef.current?.requestSubmit()}
            label="Salvar configuração"
            color="primary"
          />
        )}
        <small className="miboard-admin-field__hint">
          Última alteração fica registrada no log do sistema.
        </small>
      </form>
    );
  };
  const updateUser = async (
    id: string,
    changes: Partial<
      Pick<AdminUser, "displayName" | "role" | "status"> & { password: string }
    >,
  ) => {
    const { user: updated } = await updateAdminUser(id, changes);
    setUsers((current) =>
      current.map((item) => (item.id === id ? { ...item, ...updated } : item)),
    );
  };
  const addUser = () => {
    setUserDialog({ mode: "create", item: null });
  };
  const editUser = (item: AdminUser) => {
    setUserDialog({ mode: "edit", item });
  };
  const togglePlan = async (plan: AdminPlan) => {
    const { plan: updated } = await updateAdminPlan(plan.id, {
      active: !plan.active,
    });
    setPlans((current) =>
      current.map((item) => (item.id === plan.id ? updated : item)),
    );
  };
  const editPlan = (plan: AdminPlan) => {
    setPlanDialog({ mode: "edit", item: plan });
  };
  const addPlan = () => {
    setPlanDialog({ mode: "create", item: null });
  };
  const editCoupon = (coupon: AdminCoupon) => {
    setCouponDialog({ mode: "edit", item: coupon });
  };
  const addCoupon = () => {
    setCouponDialog({ mode: "create", item: null });
  };
  const parseMoneyToCents = (value: string) => {
    const normalized = String(value).trim().replace(/\s/g, "");
    const parsed = normalized.includes(",")
      ? Number(normalized.replace(/\./g, "").replace(",", "."))
      : Number(normalized);
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : NaN;
  };
  const serializeDateTime = (value: string) => {
    const text = String(value || "").trim();
    if (!text) {
      return null;
    }
    const candidate = text.length === 16 ? `${text}:00` : text;
    const date = new Date(candidate);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  };
  const submitUserDialog = async () => {
    if (!userDialog) {
      return;
    }
    const isCreate = userDialog.mode === "create";
    const email = userDraft.email.trim().toLowerCase();
    const displayName = userDraft.displayName.trim();
    const password = userDraft.password.trim();
    if (isCreate && (!email || password.length < 8)) {
      setError(
        "Informe um e-mail válido e uma senha com pelo menos 8 caracteres.",
      );
      return;
    }
    if (!isCreate && !displayName) {
      setError("Informe o nome do usuário.");
      return;
    }
    if (!isCreate && password && password.length < 8) {
      setError("A nova senha precisa ter pelo menos 8 caracteres.");
      return;
    }
    try {
      const resolvedDisplayName =
        displayName ||
        email.split("@")[0] ||
        userDialog.item?.displayName ||
        "";
      if (isCreate) {
        const created = await createAdminUser({
          email,
          displayName: resolvedDisplayName,
          password,
          role: userDraft.role,
          status: userDraft.status,
        });
        setUsers((current) => [created.user, ...current]);
      } else if (userDialog.item) {
        const updated = await updateAdminUser(userDialog.item.id, {
          displayName: resolvedDisplayName,
          role: userDraft.role,
          status: userDraft.status,
          ...(password ? { password } : {}),
        });
        setUsers((current) =>
          current.map((item) =>
            item.id === userDialog.item?.id
              ? { ...item, ...updated.user }
              : item,
          ),
        );
      }
      setUserDialog(null);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Não foi possível salvar o usuário.",
      );
    }
  };
  const submitPlanDialog = async () => {
    if (!planDialog) {
      return;
    }
    const name = planDraft.name.trim();
    const code = planDraft.code
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "-")
      .replace(/^-|-$/g, "");
    const monthlyPriceCents = parseMoneyToCents(planDraft.monthlyPrice);
    const annualPriceCents = parseMoneyToCents(planDraft.annualPrice);
    if (!name || !code) {
      setError("Informe um código e um nome para o plano.");
      return;
    }
    if (
      !Number.isInteger(monthlyPriceCents) ||
      monthlyPriceCents < 0 ||
      !Number.isInteger(annualPriceCents) ||
      annualPriceCents < 0
    ) {
      setError("Informe preços válidos.");
      return;
    }
    const features = planDraft.features
      .split(/\r?\n|,/)
      .map((feature) => feature.trim())
      .filter(Boolean);
    try {
      if (planDialog.mode === "create") {
        const { plan } = await createAdminPlan({
          code,
          name,
          description: planDraft.description.trim(),
          monthlyPriceCents,
          annualPriceCents,
          features,
          active: planDraft.active,
        });
        setPlans((current) => [...current, plan]);
      } else if (planDialog.item) {
        const { plan } = await updateAdminPlan(planDialog.item.id, {
          name,
          description: planDraft.description.trim(),
          monthlyPriceCents,
          annualPriceCents,
          features,
          active: planDraft.active,
        });
        setPlans((current) =>
          current.map((item) => (item.id === plan.id ? plan : item)),
        );
      }
      setPlanDialog(null);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Não foi possível salvar o plano.",
      );
    }
  };
  const submitCouponDialog = async () => {
    if (!couponDialog) {
      return;
    }
    const code = couponDraft.code.trim().toUpperCase();
    const discountValue = Number(couponDraft.discountValue);
    const maxUses = couponDraft.maxUses.trim()
      ? Number(couponDraft.maxUses)
      : null;
    const expiresAt = serializeDateTime(couponDraft.expiresAt);
    if (!code || !Number.isInteger(discountValue) || discountValue <= 0) {
      setError("Informe um código e um desconto válidos.");
      return;
    }
    if (couponDraft.discountType === "percent" && discountValue > 100) {
      setError("Desconto percentual não pode passar de 100%.");
      return;
    }
    if (maxUses !== null && (!Number.isInteger(maxUses) || maxUses < 1)) {
      setError("Limite de usos inválido.");
      return;
    }
    try {
      if (couponDialog.mode === "create") {
        const { coupon } = await createAdminCoupon({
          code,
          discountType: couponDraft.discountType,
          discountValue,
          maxUses,
          expiresAt,
          active: couponDraft.active,
        });
        setCoupons((current) => [coupon, ...current]);
      } else if (couponDialog.item) {
        const { coupon } = await updateAdminCoupon(couponDialog.item.id, {
          code,
          discountType: couponDraft.discountType,
          discountValue,
          maxUses,
          expiresAt,
          active: couponDraft.active,
        });
        setCoupons((current) =>
          current.map((item) => (item.id === coupon.id ? coupon : item)),
        );
      }
      setCouponDialog(null);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Não foi possível salvar o cupom.",
      );
    }
  };
  const submitEmailTest = async () => {
    const recipient = emailTestRecipient.trim();
    if (!recipient || !/^\S+@\S+\.\S+$/.test(recipient)) {
      setEmailTestStatus({
        kind: "error",
        message: "Informe um e-mail de destino válido para o teste.",
      });
      return;
    }
    setEmailTestStatus(null);
    try {
      await testAdminEmailIntegration(recipient);
      setEmailTestStatus({
        kind: "success",
        message: `Teste enviado com sucesso para ${recipient}.`,
      });
      setEmailTestDialogOpen(false);
    } catch (reason) {
      setEmailTestStatus({
        kind: "error",
        message:
          reason instanceof Error
            ? reason.message
            : "Não foi possível testar a integração de e-mail.",
      });
    }
  };
  const content =
    path === "/admin" && dashboard ? (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 16,
        }}
      >
        {[
          ["Usuários ativos", dashboard.active_users],
          ["Usuários desativados", dashboard.disabled_users],
          ["Painéis salvos", dashboard.boards],
          ["Assinaturas ativas", dashboard.active_subscriptions],
          ["Premium ativos", dashboard.premium_subscriptions],
        ].map(([label, value]) => (
          <article key={String(label)} className="miboard-admin-kpi">
            <small className="miboard-admin-field__label">{label}</small>
            <strong
              style={{
                display: "block",
                fontSize: 30,
                color: "#102a35",
                marginTop: 8,
              }}
            >
              {value}
            </strong>
          </article>
        ))}
      </div>
    ) : path === "/admin/coupons" ? (
      <div className="miboard-admin-grid">
        <div className="miboard-admin-button-row">
          <FilledButton
            onClick={addCoupon}
            label="Adicionar cupom"
            color="primary"
          />
        </div>
        {coupons.map((coupon) => (
          <article
            key={coupon.id}
            className="miboard-admin-card miboard-admin-card--pad"
            style={{ display: "grid", gap: 12 }}
          >
            <div style={{ display: "grid", gap: 4 }}>
              <strong>{coupon.code}</strong>
              <small className="miboard-admin-field__hint">
                {coupon.discountType === "percent"
                  ? `${coupon.discountValue}%`
                  : `R$ ${(coupon.discountValue / 100).toFixed(2)}`}{" "}
                · {coupon.usesCount}/{coupon.maxUses || "∞"} usos
              </small>
              <small className="miboard-admin-field__hint">
                {coupon.expiresAt
                  ? `Válido até ${new Date(coupon.expiresAt).toLocaleString(
                      "pt-BR",
                    )}`
                  : "Sem validade definida"}
              </small>
            </div>
            <div className="miboard-admin-button-row">
              <FilledButton
                onClick={() => editCoupon(coupon)}
                label="Editar"
                color="primary"
                variant="outlined"
              />
              <FilledButton
                label={coupon.active ? "Ativo" : "Inativo"}
                color={coupon.active ? "success" : "muted"}
                variant="outlined"
                onClick={() =>
                  updateAdminCoupon(coupon.id, { active: !coupon.active }).then(
                    ({ coupon: updated }) =>
                      setCoupons((current) =>
                        current.map((item) =>
                          item.id === updated.id ? updated : item,
                        ),
                      ),
                  )
                }
              />
            </div>
          </article>
        ))}
      </div>
    ) : path === "/admin/users" ? (
      <div className="miboard-admin-grid">
        <div className="miboard-admin-button-row">
          <FilledButton onClick={addUser} label="Adicionar usuário" />
        </div>
        {users.map((item) => (
          <article
            key={item.id}
            className="miboard-admin-card miboard-admin-card--pad"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 130px 130px auto",
              gap: 12,
              alignItems: "center",
            }}
          >
            <div>
              <strong>{item.displayName}</strong>
              <br />
              <small>{item.email}</small>
            </div>
            <select
              value={item.role}
              className="miboard-admin-control"
              onChange={(event) =>
                updateUser(item.id, {
                  role: event.target.value as AdminUser["role"],
                })
              }
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <select
              value={item.status}
              className="miboard-admin-control"
              onChange={(event) =>
                updateUser(item.id, {
                  status: event.target.value as AdminUser["status"],
                })
              }
            >
              <option value="active">Ativo</option>
              <option value="disabled">Desativado</option>
            </select>
            <div className="miboard-admin-button-row">
              <FilledButton
                onClick={() => editUser(item)}
                label="Editar"
                variant="outlined"
                color="primary"
              />
              <FilledButton
                label="Desativar"
                color="danger"
                variant="outlined"
                disabled={item.status === "disabled"}
                onClick={() =>
                  disableAdminUser(item.id).then(() =>
                    updateUser(item.id, { status: "disabled" }),
                  )
                }
              />
            </div>
          </article>
        ))}
      </div>
    ) : path === "/admin/plans" ? (
      <div className="miboard-admin-grid">
        <div className="miboard-admin-button-row">
          <FilledButton
            onClick={addPlan}
            label="Adicionar plano"
            color="primary"
          />
        </div>
        <div className="miboard-admin-grid miboard-admin-grid--cards">
          {plans.map((plan) => (
            <article
              key={plan.id}
              className="miboard-admin-card miboard-admin-card--pad"
            >
              <h2>{plan.name}</h2>
              <div className="miboard-admin-button-row">
                <FilledButton
                  onClick={() => editPlan(plan)}
                  label="Editar"
                  color="primary"
                  variant="outlined"
                />
                <FilledButton
                  onClick={() => togglePlan(plan)}
                  label={plan.active ? "Ativo" : "Inativo"}
                  color={plan.active ? "success" : "muted"}
                  variant="outlined"
                />
              </div>
              <p>{plan.description}</p>
              <strong>
                {plan.monthlyPriceCents
                  ? `R$ ${(plan.monthlyPriceCents / 100).toFixed(2)}/mês`
                  : "Gratuito"}
              </strong>
              <p>
                {plan.annualPriceCents
                  ? `R$ ${(plan.annualPriceCents / 100).toFixed(2)}/ano`
                  : ""}
              </p>
              <small className="miboard-admin-field__hint">
                Código: {plan.code} ·{" "}
                {plan.active ? "Plano ativo" : "Plano inativo"}
              </small>
              <ul>
                {plan.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    ) : path === "/admin/orders" ? (
      <div className="miboard-admin-grid">
        {orders.length === 0 ? (
          <p className="miboard-admin-field__hint">Nenhum pedido registrado.</p>
        ) : (
          orders.map((order) => (
            <article
              key={order.id}
              className="miboard-admin-card miboard-admin-card--pad"
            >
              <strong>{order.planName || "Plano"}</strong> ·{" "}
              {order.email || "Sem usuário"} · R${" "}
              {(order.amountCents / 100).toFixed(2)}
              <br />
              <small>
                {order.status} · {order.provider || "gateway não definido"} ·{" "}
                {new Date(order.createdAt).toLocaleString("pt-BR")}
              </small>
            </article>
          ))
        )}
      </div>
    ) : path === "/admin/carts" ? (
      <div className="miboard-admin-grid">
        {carts.length === 0 ? (
          <p className="miboard-admin-field__hint">
            Nenhum carrinho registrado.
          </p>
        ) : (
          carts.map((cart) => (
            <article
              key={cart.id}
              className="miboard-admin-card miboard-admin-card--pad"
            >
              <strong>{cart.email || "Sem usuário"}</strong> · {cart.status} ·{" "}
              {Array.isArray(cart.items) ? cart.items.length : 0} itens
              <br />
              <small>
                Atualizado em {new Date(cart.updatedAt).toLocaleString("pt-BR")}
              </small>
            </article>
          ))
        )}
      </div>
    ) : path.endsWith("-logs") ? (
      <div className="miboard-admin-grid">
        {logs.length === 0 ? (
          <p className="miboard-admin-field__hint">
            Nenhum registro encontrado.
          </p>
        ) : (
          logs.slice(logsPage * 30, logsPage * 30 + 30).map((log) => (
            <article
              key={log.id}
              className="miboard-admin-card miboard-admin-card--pad"
            >
              <strong>{log.level.toUpperCase()}</strong> · {log.message}
              <br />
              {(Boolean(log.metadata?.email) || Boolean(log.metadata?.ip)) && (
                <small>
                  {String(log.metadata?.email || "Sem e-mail")}
                  {log.metadata?.ip ? ` · IP ${String(log.metadata.ip)}` : ""}
                  <br />
                </small>
              )}
              <small>{new Date(log.createdAt).toLocaleString("pt-BR")}</small>
            </article>
          ))
        )}
        {logs.length > 30 && (
          <div className="miboard-admin-button-row">
            <FilledButton
              onClick={() => setLogsPage((page) => Math.max(0, page - 1))}
              label="Anterior"
              color="primary"
              variant="outlined"
            />
            <span className="miboard-admin-field__hint">
              Página {logsPage + 1} de {Math.ceil(logs.length / 30)}
            </span>
            <FilledButton
              onClick={() =>
                setLogsPage((page) =>
                  Math.min(Math.ceil(logs.length / 30) - 1, page + 1),
                )
              }
              label="Próxima"
              color="primary"
              variant="outlined"
            />
          </div>
        )}
      </div>
    ) : (
      settingPage() || (
        <div>
          <h2>Módulo em configuração</h2>
          <p>
            Este módulo já está conectado ao banco e aguardando os primeiros
            registros.
          </p>
        </div>
      )
    );

  if (error) {
    return <main style={{ padding: 40 }}>{error}</main>;
  }
  if (!user) {
    return (
      <main className="miboard-loading-screen">
        <LoadingClock label="Carregando administração..." />
      </main>
    );
  }

  return (
    <main
      className="excalidraw miboard-admin-shell"
      style={{
        ...shellStyles.page,
        overflowY: "auto",
      }}
    >
      <aside
        style={{
          ...shellStyles.sidebar,
          height: "100vh",
          overflowY: "auto",
          position: "sticky",
          top: 0,
          alignSelf: "start",
        }}
      >
        <a
          href="/app/boards"
          style={{
            color: "white",
            fontSize: 22,
            fontWeight: 800,
            textDecoration: "none",
          }}
        >
          <img
            src="/image/Logo%20Miboard.png"
            alt="MiBoard"
            style={{ width: 128, height: "auto", objectFit: "contain" }}
          />
        </a>
        <p style={{ color: "#b7d4de", fontSize: 13 }}>{user.profile.email}</p>
        <FilledButton
          onClick={() => window.location.assign("/app/boards")}
          label="Ir para meus painéis"
          color="primary"
          fullWidth
        />
        {sections.map((section) => (
          <section key={section.title} style={{ marginTop: 28 }}>
            <h2
              style={{
                color: "#76d1eb",
                fontSize: 12,
                letterSpacing: ".08em",
                textTransform: "uppercase",
              }}
            >
              {section.title}
            </h2>
            {section.items.map(([href, label]) => (
              <a
                key={href}
                href={`/app${href}`}
                style={{
                  display: "block",
                  padding: "9px 10px",
                  borderRadius: 8,
                  color: href === path ? "#102a35" : "#e7f7fb",
                  background: href === path ? "#7ad9f1" : "transparent",
                  textDecoration: "none",
                  fontSize: 14,
                }}
              >
                {label}
              </a>
            ))}
          </section>
        ))}
        <button
          type="button"
          onClick={() => {
            logout();
            window.location.assign(
              `/app/login?next=${encodeURIComponent(window.location.pathname)}`,
            );
          }}
          style={{
            marginTop: 28,
            border: 0,
            background: "transparent",
            color: "#b7d4de",
            cursor: "pointer",
          }}
        >
          Sair
        </button>
      </aside>
      <section style={shellStyles.content}>
        <p style={{ color: "#58727c", margin: 0 }}>Administração</p>
        <h1 style={{ marginTop: 8, color: "#102a35" }}>{activeLabel}</h1>
        <div className="miboard-admin-card miboard-admin-card--soft miboard-admin-card--pad">
          {content}
        </div>
      </section>
      {userDialog && (
        <MiBoardDialog
          title={
            userDialog.mode === "create"
              ? "Adicionar usuário"
              : "Editar usuário"
          }
          onClose={() => setUserDialog(null)}
          actions={
            <>
              <button
                type="button"
                onClick={() => setUserDialog(null)}
                className="miboard-cancel-button"
              >
                Cancelar
              </button>
              <button
                type="button"
                className="miboard-save-button"
                onClick={() => userFormRef.current?.requestSubmit()}
              >
                Salvar usuário
              </button>
            </>
          }
        >
          <form
            ref={userFormRef}
            className="miboard-admin-grid"
            onSubmit={(event) => {
              event.preventDefault();
              submitUserDialog();
            }}
          >
            {userDialog.mode === "create" && (
              <label className="miboard-admin-field">
                <span className="miboard-admin-field__label">
                  E-mail do usuário
                </span>
                <input
                  autoFocus
                  name="email"
                  className="miboard-admin-control"
                  type="email"
                  value={userDraft.email}
                  onChange={(event) =>
                    setUserDraft((current) => ({
                      ...current,
                      email: event.target.value,
                    }))
                  }
                />
              </label>
            )}
            <label className="miboard-admin-field">
              <span className="miboard-admin-field__label">Nome</span>
              <input
                autoFocus={userDialog.mode === "edit"}
                name="displayName"
                className="miboard-admin-control"
                value={userDraft.displayName}
                onChange={(event) =>
                  setUserDraft((current) => ({
                    ...current,
                    displayName: event.target.value,
                  }))
                }
              />
            </label>
            <div className="miboard-admin-grid miboard-admin-grid--cards">
              <label className="miboard-admin-field">
                <span className="miboard-admin-field__label">Perfil</span>
                <select
                  name="role"
                  className="miboard-admin-control"
                  value={userDraft.role}
                  onChange={(event) =>
                    setUserDraft((current) => ({
                      ...current,
                      role: event.target.value as AdminUser["role"],
                    }))
                  }
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </label>
              <label className="miboard-admin-field">
                <span className="miboard-admin-field__label">Status</span>
                <select
                  name="status"
                  className="miboard-admin-control"
                  value={userDraft.status}
                  onChange={(event) =>
                    setUserDraft((current) => ({
                      ...current,
                      status: event.target.value as AdminUser["status"],
                    }))
                  }
                >
                  <option value="active">Ativo</option>
                  <option value="disabled">Desativado</option>
                </select>
              </label>
            </div>
            <label className="miboard-admin-field">
              <span className="miboard-admin-field__label">
                {userDialog.mode === "create"
                  ? "Senha temporária"
                  : "Nova senha (opcional)"}
              </span>
              <PasswordField
                name="password"
                inputClassName="miboard-admin-control"
                placeholder={
                  userDialog.mode === "create"
                    ? "Mínimo de 8 caracteres"
                    : "Deixe em branco para não alterar"
                }
                value={userDraft.password}
                onChange={(event) =>
                  setUserDraft((current) => ({
                    ...current,
                    password: event.target.value,
                  }))
                }
              />
            </label>
          </form>
        </MiBoardDialog>
      )}
      {planDialog && (
        <MiBoardDialog
          title={
            planDialog.mode === "create" ? "Adicionar plano" : "Editar plano"
          }
          onClose={() => setPlanDialog(null)}
          actions={
            <>
              <button
                type="button"
                onClick={() => setPlanDialog(null)}
                className="miboard-cancel-button"
              >
                Cancelar
              </button>
              <button
                type="button"
                className="miboard-save-button"
                onClick={() => planFormRef.current?.requestSubmit()}
              >
                Salvar plano
              </button>
            </>
          }
        >
          <form
            ref={planFormRef}
            className="miboard-admin-grid"
            onSubmit={(event) => {
              event.preventDefault();
              submitPlanDialog();
            }}
          >
            <div className="miboard-admin-grid miboard-admin-grid--cards">
              <label className="miboard-admin-field">
                <span className="miboard-admin-field__label">Código</span>
                <input
                  name="code"
                  className="miboard-admin-control"
                  value={planDraft.code}
                  disabled={planDialog.mode === "edit"}
                  onChange={(event) =>
                    setPlanDraft((current) => ({
                      ...current,
                      code: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="miboard-admin-field">
                <span className="miboard-admin-field__label">Nome</span>
                <input
                  autoFocus
                  name="name"
                  className="miboard-admin-control"
                  value={planDraft.name}
                  onChange={(event) =>
                    setPlanDraft((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <label className="miboard-admin-field">
              <span className="miboard-admin-field__label">Descrição</span>
              <textarea
                name="description"
                rows={4}
                className="miboard-admin-control"
                value={planDraft.description}
                onChange={(event) =>
                  setPlanDraft((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
              />
            </label>
            <div className="miboard-admin-grid miboard-admin-grid--cards">
              <label className="miboard-admin-field">
                <span className="miboard-admin-field__label">Preço mensal</span>
                <input
                  name="monthlyPrice"
                  className="miboard-admin-control"
                  inputMode="decimal"
                  value={planDraft.monthlyPrice}
                  onChange={(event) =>
                    setPlanDraft((current) => ({
                      ...current,
                      monthlyPrice: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="miboard-admin-field">
                <span className="miboard-admin-field__label">Preço anual</span>
                <input
                  name="annualPrice"
                  className="miboard-admin-control"
                  inputMode="decimal"
                  value={planDraft.annualPrice}
                  onChange={(event) =>
                    setPlanDraft((current) => ({
                      ...current,
                      annualPrice: event.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <label className="miboard-admin-field">
              <span className="miboard-admin-field__label">
                Benefícios do plano
              </span>
              <textarea
                name="features"
                rows={6}
                className="miboard-admin-control"
                value={planDraft.features}
                onChange={(event) =>
                  setPlanDraft((current) => ({
                    ...current,
                    features: event.target.value,
                  }))
                }
                placeholder="Um benefício por linha ou separado por vírgula"
              />
            </label>
            <label className="miboard-admin-field">
              <span className="miboard-admin-field__label">Plano ativo</span>
              <select
                name="active"
                className="miboard-admin-control"
                value={String(planDraft.active)}
                onChange={(event) =>
                  setPlanDraft((current) => ({
                    ...current,
                    active: event.target.value === "true",
                  }))
                }
              >
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </label>
          </form>
        </MiBoardDialog>
      )}
      {couponDialog && (
        <MiBoardDialog
          title={
            couponDialog.mode === "create" ? "Adicionar cupom" : "Editar cupom"
          }
          onClose={() => setCouponDialog(null)}
          actions={
            <>
              <button
                type="button"
                onClick={() => setCouponDialog(null)}
                className="miboard-cancel-button"
              >
                Cancelar
              </button>
              <button
                type="button"
                className="miboard-save-button"
                onClick={() => couponFormRef.current?.requestSubmit()}
              >
                Salvar cupom
              </button>
            </>
          }
        >
          <form
            ref={couponFormRef}
            className="miboard-admin-grid"
            onSubmit={(event) => {
              event.preventDefault();
              submitCouponDialog();
            }}
          >
            <div className="miboard-admin-grid miboard-admin-grid--cards">
              <label className="miboard-admin-field">
                <span className="miboard-admin-field__label">Código</span>
                <input
                  autoFocus
                  name="code"
                  className="miboard-admin-control"
                  value={couponDraft.code}
                  onChange={(event) =>
                    setCouponDraft((current) => ({
                      ...current,
                      code: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="miboard-admin-field">
                <span className="miboard-admin-field__label">Tipo</span>
                <select
                  name="discountType"
                  className="miboard-admin-control"
                  value={couponDraft.discountType}
                  onChange={(event) =>
                    setCouponDraft((current) => ({
                      ...current,
                      discountType: event.target
                        .value as AdminCoupon["discountType"],
                    }))
                  }
                >
                  <option value="percent">Percentual</option>
                  <option value="fixed">Fixo em centavos</option>
                </select>
              </label>
            </div>
            <div className="miboard-admin-grid miboard-admin-grid--cards">
              <label className="miboard-admin-field">
                <span className="miboard-admin-field__label">Desconto</span>
                <input
                  name="discountValue"
                  className="miboard-admin-control"
                  inputMode="numeric"
                  value={couponDraft.discountValue}
                  onChange={(event) =>
                    setCouponDraft((current) => ({
                      ...current,
                      discountValue: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="miboard-admin-field">
                <span className="miboard-admin-field__label">
                  Limite de usos
                </span>
                <input
                  name="maxUses"
                  className="miboard-admin-control"
                  inputMode="numeric"
                  value={couponDraft.maxUses}
                  onChange={(event) =>
                    setCouponDraft((current) => ({
                      ...current,
                      maxUses: event.target.value,
                    }))
                  }
                  placeholder="Deixe em branco para ilimitado"
                />
              </label>
            </div>
            <label className="miboard-admin-field">
              <span className="miboard-admin-field__label">Validade</span>
              <input
                name="expiresAt"
                className="miboard-admin-control"
                type="datetime-local"
                value={couponDraft.expiresAt}
                onChange={(event) =>
                  setCouponDraft((current) => ({
                    ...current,
                    expiresAt: event.target.value,
                  }))
                }
              />
            </label>
            <label className="miboard-admin-field">
              <span className="miboard-admin-field__label">Cupom ativo</span>
              <select
                name="active"
                className="miboard-admin-control"
                value={String(couponDraft.active)}
                onChange={(event) =>
                  setCouponDraft((current) => ({
                    ...current,
                    active: event.target.value === "true",
                  }))
                }
              >
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </label>
          </form>
        </MiBoardDialog>
      )}
      {emailTestDialogOpen && (
        <MiBoardDialog
          title="Testar integração de e-mail"
          onClose={() => setEmailTestDialogOpen(false)}
          actions={
            <>
              <button
                type="button"
                onClick={() => setEmailTestDialogOpen(false)}
                className="miboard-cancel-button"
              >
                Cancelar
              </button>
              <button
                type="button"
                className="miboard-save-button"
                onClick={() => emailTestFormRef.current?.requestSubmit()}
              >
                Enviar teste
              </button>
            </>
          }
        >
          <form
            ref={emailTestFormRef}
            className="miboard-admin-grid"
            onSubmit={(event) => {
              event.preventDefault();
              submitEmailTest();
            }}
          >
            <label className="miboard-admin-field">
              <span className="miboard-admin-field__label">
                E-mail de destino
              </span>
              <input
                autoFocus
                type="email"
                className="miboard-admin-control"
                value={emailTestRecipient}
                onChange={(event) => setEmailTestRecipient(event.target.value)}
                placeholder="admin@exemplo.com"
              />
            </label>
            <p className="miboard-admin-field__hint" style={{ margin: 0 }}>
              O teste usa as configurações salvas em Integração de e-mail e
              envia uma mensagem real para validar a conexão com o serviço.
            </p>
          </form>
        </MiBoardDialog>
      )}
    </main>
  );
};
